import { create } from "zustand";

/**
 * Single, minimal Zustand store for mines (hard rule: one store per app).
 *
 *   idle → playing → (busted | cashed)
 *
 * Every reveal is a server round-trip (the D1 ledger is the anti-cheat); this store
 * only mirrors what the server confirmed. value semantics: multiplier ×100.
 */

export type Phase = "idle" | "playing" | "busted" | "cashed";

export interface CashResult {
  mult: number;
  gems: number;
  value: number;
  rank: number;
  total: number;
  percentile: number;
}

interface MinesState {
  phase: Phase;
  roundId: string | null;
  /** Server-confirmed safe picks, reveal order. */
  picks: number[];
  /** Multiplier after the confirmed picks (1 when none). */
  mult: number;
  /** Full mine layout — revealed only after a bust. */
  mines: number[] | null;
  /** The pick that blew up (for the red tile). */
  fatalPick: number | null;
  cashResult: CashResult | null;
  /** Best cashed multiplier ever (×100), localStorage-backed. */
  best: number;

  begin: (roundId: string) => void;
  confirmReveal: (pick: number, mult: number) => void;
  bust: (fatalPick: number, mines: number[]) => void;
  cashed: (result: CashResult) => void;
  reset: () => void;
}

const BEST_KEY = "kp:mines:best";
function loadBest(): number {
  try {
    return Number.parseInt(localStorage.getItem(BEST_KEY) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}
function saveBest(n: number): void {
  try {
    localStorage.setItem(BEST_KEY, String(n));
  } catch {
    /* blocked — fine */
  }
}

export const useMinesStore = create<MinesState>((set, get) => ({
  phase: "idle",
  roundId: null,
  picks: [],
  mult: 1,
  mines: null,
  fatalPick: null,
  cashResult: null,
  best: loadBest(),

  begin: (roundId) =>
    set({
      phase: "playing",
      roundId,
      picks: [],
      mult: 1,
      mines: null,
      fatalPick: null,
      cashResult: null,
    }),

  confirmReveal: (pick, mult) =>
    set((s) => ({ picks: [...s.picks, pick], mult })),

  bust: (fatalPick, mines) => set({ phase: "busted", fatalPick, mines }),

  cashed: (result) => {
    const nextBest = Math.max(get().best, result.value);
    if (nextBest > get().best) saveBest(nextBest);
    set({ phase: "cashed", cashResult: result, best: nextBest });
  },

  reset: () =>
    set({
      phase: "idle",
      roundId: null,
      picks: [],
      mult: 1,
      mines: null,
      fatalPick: null,
      cashResult: null,
      best: loadBest(),
    }),
}));
